import { monthRange } from './csv.js';
import { UNIFLOW_TIMEZONE, wallClockAt, wallTimeToUtc } from './tz.js';
/**
 * Filtrer « du … au … », au jour près (VUE-16).
 *
 * Un jour est écrit `AAAA-MM-JJ` et désigne un jour **du calendrier suisse** : du jour à
 * 00 h 00 au lendemain à 00 h 00, heure du mur. Le « au » est **compris** — c'est ce qu'on
 * entend quand on dit « du 1ᵉʳ au 31 août ».
 *
 * 🪤 C'est la leçon de décembre, une quatrième fois : des bornes posées en UTC décaleraient
 * le filtre d'une à deux heures, et le travail du 1ᵉʳ à 00 h 07 tomberait dans la période
 * d'avant. Les bornes se calculent ici, en un seul endroit, avec le fuseau des données.
 */
export const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
function parseDay(value) {
    const m = DAY_PATTERN.exec(value);
    if (!m)
        return null;
    const year = +m[1];
    const month = +m[2];
    const day = +m[3];
    // Un 31 avril ou un 29 février d'une année ordinaire passerait l'expression régulière :
    // on le fait relire par le calendrier, qui le reporterait au mois suivant.
    const check = new Date(Date.UTC(year, month - 1, day));
    if (check.getUTCFullYear() !== year ||
        check.getUTCMonth() !== month - 1 ||
        check.getUTCDate() !== day) {
        return null;
    }
    return { year, month, day };
}
/** Vrai pour un jour qui existe au calendrier, écrit `AAAA-MM-JJ`. */
export function isDay(value) {
    return parseDay(value) !== null;
}
const pad = (n) => String(n).padStart(2, '0');
const format = ({ year, month, day }) => `${year}-${pad(month)}-${pad(day)}`;
/** Le lendemain, au calendrier — sans passer par une heure, donc sans fuseau. */
function nextDay({ year, month, day }) {
    const d = new Date(Date.UTC(year, month - 1, day + 1));
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}
/**
 * Les instants qui bornent « du `from` au `to` compris », à l'heure du mur.
 * Une borne absente n'en pose pas ; une borne illisible lève une erreur — le schéma d'API
 * les a refusées avant.
 */
export function dayRange(from, to, timeZone = UNIFLOW_TIMEZONE) {
    const out = {};
    if (from) {
        const p = parseDay(from);
        if (!p)
            throw new Error(`Jour invalide : « ${from} » (attendu AAAA-MM-JJ)`);
        out.start = wallTimeToUtc(p.year, p.month, p.day, 0, 0, 0, timeZone);
    }
    if (to) {
        const p = parseDay(to);
        if (!p)
            throw new Error(`Jour invalide : « ${to} » (attendu AAAA-MM-JJ)`);
        const n = nextDay(p);
        out.endExclusive = wallTimeToUtc(n.year, n.month, n.day, 0, 0, 0, timeZone);
    }
    return out;
}
/** Le premier et le dernier jour d'un mois « AAAA-MM » — ce qu'un raccourci « ce mois » pose. */
export function monthDays(period) {
    const m = /^(\d{4})-(\d{2})$/.exec(period);
    if (!m)
        throw new Error(`Période invalide : « ${period} » (attendu AAAA-MM)`);
    const year = +m[1];
    const month = +m[2];
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return { from: `${m[1]}-${m[2]}-01`, to: format({ year, month, day: last }) };
}
/** Le jour qu'il est **ici**, `AAAA-MM-JJ`. */
export function todayLocal(now = new Date(), timeZone = UNIFLOW_TIMEZONE) {
    const { year, month, day } = wallClockAt(now, timeZone);
    return format({ year, month, day });
}
/**
 * La fenêtre de dates d'une requête, prête pour un `where` : `{ gte, lt }`.
 *
 * `period` (un mois entier) reste accepté et **prioritaire** : c'était le seul filtre de
 * dates jusqu'au 24.09.2026, et un lien partagé avant cette date doit toujours rouvrir le
 * même écran (VUE-03). `null` quand aucune date n'est posée.
 */
export function occurredWindow(query, timeZone = UNIFLOW_TIMEZONE) {
    if (query.period) {
        const { start, endExclusive } = monthRange(query.period, timeZone);
        return { gte: start, lt: endExclusive };
    }
    if (!query.from && !query.to)
        return null;
    const { start, endExclusive } = dayRange(query.from, query.to, timeZone);
    return {
        ...(start ? { gte: start } : {}),
        ...(endExclusive ? { lt: endExclusive } : {}),
    };
}
